import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface WhatcomDocument {
  documentNumber: string;
  filingDate: string;
  grantors: string[];
  grantees: string[];
  lender: string; // The identified actual lender (after filtering title cos, MERS, etc.)
  docType: string;
  legalDescription: string;
  parcelId: string;
}

interface Props {
  onRecordsSaved: () => void;
  county?: string;
}

// Title companies, trustees, and MERS — these are NOT the actual lender
const TITLE_AND_TRUSTEE_KEYWORDS = [
  'TITLE INSURANCE', 'TITLE COMPANY', 'TITLE CO', 'TITLE LLC',
  'CHICAGO TITLE', 'FIRST AMERICAN TITLE', 'FIDELITY NATIONAL TITLE',
  'OLD REPUBLIC NATIONAL TITLE', 'STEWART TITLE',
  'WHATCOM LAND TITLE', 'GUARDIAN NORTHWEST TITLE', 'CW TITLE',
  'SAN JUAN TITLE', 'ALLIANCE TITLE', 'FRIDAY HARBOR ESCROW',
  'T.D. ESCROW', 'T.D. SERVICE',
  'NEXTITLE', 'PARKWAY TITLE', 'CLEAREDGE TITLE',
  'MORTGAGE ELECTRONIC REGISTRATION', 'MERS',
  'TRUSTEE SERVICES', 'QUALITY LOAN SERVICE',
  'RECONVEYANCE PROFESSIONALS',
  'BISHOP, WHITE, MARSHALL', 'MCCARTHY & HOLTHUS',
  'ALDRIDGE PITE', 'MALCOM & CISNEROS', 'WEINSTEIN & REILY', 'WEINSTEIN & RILEY',
  'WFG LENDER SERVICES', 'UPF WASHINGTON',
  'WASHINGTON SERVICES INC', 'WASHINGTON SERVICES, INC',
  'NORTHWEST FINANCIAL CORPORATION',
  'TIMIOS',
];

// Corporate/institutional lender keywords
const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'NAVY FEDERAL', 'BOEING EMPLOYEES',
  'GUILD', 'FAIRWAY', 'NEWREZ', 'FLAGSTAR',
  'UNITED WHOLESALE', 'ROCKET MORTGAGE', 'MR COOPER',
  'PRIMELENDING', 'PROVIDENT FUNDING', 'GUARANTEED RATE',
  'GATEWAY MORTGAGE', 'JPMORGAN', 'THRIVE MORTGAGE',
  'CORNERSTONE HOME', 'WATERSTONE MORTGAGE',
  'HIGHLANDS RESIDENTIAL', 'MOVEMENT MORTGAGE', 'CROSSCOUNTRY MORTGAGE',
  'CROSS COUNTRY MORTGAGE',
  'FREEDOM MORTGAGE', 'AMERISAVE', 'BETTER MORTGAGE', 'WILLOW BEND',
  'DAS ACQUISITION', 'GUARDIAN MORTGAGE',
  'HOUSING & URBAN', 'HUD', 'SECRETARY OF HOUSING',
  'RURAL HOUSING SERVICE', 'RURAL DEVELOPMENT',
  'UNITED STATES OF AMERICA', 'DEPARTMENT OF AGRICULTURE',
  'HOUSING FINANCE COMMISSION',
  'PENNYMAC LOAN', 'LOANDEPOT',
  'CALIBER HOME', 'QUICKEN LOANS',
  'HOME POINT', 'CARDINAL FINANCIAL', 'NATIONS LENDING',
  'CMG MORTGAGE', 'CMG HOME LOANS',
  'EVERGREEN MONEYSOURCE', 'SAMMAMISH MORTGAGE',
  'HOMEBRIDGE FINANCIAL', 'REAL ESTATE MORTGAGE NETWORK',
  'PEOPLES BANK', 'SAVIBANK', 'BANNER BANK', 'HERITAGE BANK',
  'HOMESTREET BANK', 'MOUNTAIN PACIFIC BANK',
  'INDUSTRIAL CREDIT UNION', 'WHATCOM EDUCATIONAL CREDIT UNION',
  'NORTH COAST CREDIT UNION', 'SOUND CREDIT UNION', 'TWINSTAR CREDIT UNION',
  'SOUND COMMUNITY BANK',
  'RALLY CREDIT UNION', 'PENTAGON FEDERAL', 'GEORGIAS OWN CREDIT UNION',
  'WASHINGTON STATE EMPLOYEES CREDIT UNION', 'SALAL CREDIT UNION',
  'ALLIANT CREDIT UNION', 'COLUMBIA CREDIT UNION', 'DISCOVER BANK',
  'USAA FEDERAL', 'USAA,', 'ALLY BANK',
  'FIRST FED BANK', 'BMO BANK', 'RBC BANK', 'NBKC BANK',
  'WASHINGTON FEDERAL', 'ACCESS BANK', 'SOFI BANK',
  'KEYBANK', 'PNC BANK', 'BANK OF AMERICA', 'US BANK', 'U S BANK', 'U.S. BANK',
  'FIRST INTERSTATE BANK', 'ALASKA USA',
  'GOLD STAR MORTGAGE', 'INSPIRE HOME LOANS',
  'AMERICAN PACIFIC MORTGAGE', 'CANOPY MORTGAGE',
  'ACADEMY MORTGAGE', 'ROYAL UNITED MORTGAGE',
  'CARRINGTON MORTGAGE', 'NEW AMERICAN FUNDING',
  'BAY EQUITY', 'SPRING EQ', 'FIGURE LENDING',
  'HOMEXPRESS MORTGAGE', 'HOMETOWN EQUITY MORTGAGE',
  'PEAK MORTGAGE', 'HOMETAP EQUITY',
  'GOODLEAP', 'THIRD FEDERAL', 'NATIONSTAR',
  'NORTHWEST SUPERIOR HOMES',
  'SILVER HILL FUNDING', 'LEVEL CAPITAL',
  'RESIDENTIAL WHOLESALE MORTGAGE',
  'OCELOT DEVELOPMENT', 'HACIENDA WEST',
  'CHURCH DEVELOPMENT FUND',
  'TEXAS STATE AFFORDABLE',
  'COMMUNITY DEVELOPMENT CORP',
  'NATIONS RELIABLE LENDING',
  'VILLAGE CAPITAL',
  'PARAMOUNT RESIDENTIAL',
  'DIRECTIONS EQUITY',
  'HALLMARK HOME MORTGAGE',
  'KEY HOME MORTGAGE',
  'LUZMA DEVELOPMENT',
  'MONTANARO INVESTMENTS',
  'ADELANTE FUNDING',
  'FRANK A G INVESTMENTS',
  'OPAL COMMUNITY LAND TRUST',
  'WASHINGTON STATE DEPARTMENT',
  'OPPORTUNITY COUNCIL',
  'SAN JUAN COMMUNITY HOME TRUST',
  'FIRST TECHNOLOGY FEDERAL',
  'CHERRY CREEK MORTGAGE',
];

const isTitleOrMERS = (name: string): boolean => {
  const upper = name.toUpperCase();
  return TITLE_AND_TRUSTEE_KEYWORDS.some(kw => upper.includes(kw));
};

const isCorpLender = (name: string): boolean => {
  const upper = name.toUpperCase();
  return CORPORATE_KEYWORDS.some(kw => {
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`\\b${escaped}\\b`, 'i');
    return pattern.test(upper);
  });
};

export function WhatcomIndexParser({ onRecordsSaved, county = 'Whatcom' }: Props) {
  // countyName is for display only; use county value (lowercase) for DB storage to match filter
  const countyName = county.toLowerCase() === 'sanjuan' ? 'San Juan'
    : county.charAt(0).toUpperCase() + county.slice(1).toLowerCase();
  const countyDbValue = county.toLowerCase();
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const parseWhatcomData = (text: string): WhatcomDocument[] => {
    const documents: WhatcomDocument[] = [];
    const seenDocNumbers = new Set<string>();

    // Split into record blocks — each record starts with a document number pattern
    const blocks = text.split(/(?=\d{4}-\d{7})/);

    for (const block of blocks) {
      const trimmed = block.trim();
      if (!trimmed) continue;

      // Extract document number
      const docNumMatch = trimmed.match(/^(\d{4}-\d{7})/);
      if (!docNumMatch) continue;
      const documentNumber = docNumMatch[1];

      // Must be Deed of Trust
      if (!/Deed of Trust/i.test(trimmed)) continue;

      // Skip if it also contains "Assignment of Rents" or "UCC" with multiple doc types
      // But still allow "Deed of Trust, UCC" since those can be owner-financed

      // Extract date
      const dateMatch = trimmed.match(/Date Recorded:\s*(\d{1,2}\/\d{1,2}\/\d{4})/i);
      if (!dateMatch) continue;
      const rawDate = dateMatch[1];
      const dateParts = rawDate.split('/');
      const filingDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;

      // Extract all grantors (GRANTOR: or DIRECT:)
      const grantors: string[] = [];
      const grantorRegex = /(?:GRANTOR|DIRECT):\s*(.+)/gi;
      let grantorMatch;
      while ((grantorMatch = grantorRegex.exec(trimmed)) !== null) {
        grantors.push(grantorMatch[1].trim());
      }

      // Extract all grantees (GRANTEE: or INDIRECT:)
      const grantees: string[] = [];
      const granteeRegex = /(?:GRANTEE|INDIRECT):\s*(.+)/gi;
      let granteeMatch;
      while ((granteeMatch = granteeRegex.exec(trimmed)) !== null) {
        grantees.push(granteeMatch[1].trim());
      }

      if (grantees.length === 0) continue;

      // Extract APN or Tax#
      const apnMatch = trimmed.match(/(?:APN#|Tax#)\s*(\d+)/);
      const parcelId = apnMatch ? apnMatch[1] : '';

      // Extract legal description (everything after APN# or Tax# on that line)
      const legalLine = trimmed.match(/(?:APN#|Tax#).+/);
      let legalDescription = '';
      if (legalLine) {
        legalDescription = legalLine[0].replace(/(?:APN#|Tax#)\s*\d+\s*/, '').trim();
      }

      // Identify the REAL lender: filter out title companies, MERS, law firms, trustee services
      const realLenders = grantees.filter(g => !isTitleOrMERS(g));

      // If no real lenders found after filtering, skip (all grantees are title/MERS)
      if (realLenders.length === 0) continue;

      // The lender is the first non-title, non-MERS grantee
      const lender = realLenders[0];

      // Deduplicate
      if (seenDocNumbers.has(documentNumber)) continue;
      seenDocNumbers.add(documentNumber);

      documents.push({
        documentNumber,
        filingDate,
        grantors,
        grantees,
        lender,
        docType: 'DEED OF TRUST',
        legalDescription,
        parcelId,
      });
    }

    return documents;
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({ title: "No data", description: "Please paste records", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    try {
      const documents = parseWhatcomData(textData);
      if (documents.length === 0) {
        toast({ title: "No records found", description: "Could not parse any DEED OF TRUST records from the pasted text.", variant: "destructive" });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Filter: remove records with corporate/institutional lenders
      const potentialOwnerFinanced = documents.filter(doc => !isCorpLender(doc.lender));
      const corporateCount = documents.length - potentialOwnerFinanced.length;

      console.log(`${countyName}: Parsed ${documents.length} DOTs, ${corporateCount} corporate lenders, ${potentialOwnerFinanced.length} potential owner-financed`);

      if (potentialOwnerFinanced.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${documents.length} records — all had corporate/institutional lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Send remaining to AI for final verification
      const deedText = potentialOwnerFinanced.map(d =>
        `Document: ${d.documentNumber}\n` +
        `Date: ${d.filingDate}\n` +
        `Grantor (Borrower): ${d.grantors.join('; ')}\n` +
        `Grantee (Lender/Beneficiary): ${d.lender}\n` +
        `All Grantees: ${d.grantees.join('; ')}\n` +
        `Legal: ${d.legalDescription}\n`
      ).join('\n---\n');

      const { data: filterData, error: filterError } = await supabase.functions.invoke(
        'filter-owner-finance',
        { body: { deedText } }
      );

      if (filterError) throw filterError;

      const ownerFinancedDeals = filterData?.filteredDeals || [];
      const ownerFinancedDocNumbers = new Set(
        ownerFinancedDeals.map((deal: any) => deal.documentNumber)
      );

      const confirmedRecords = potentialOwnerFinanced.filter(d =>
        ownerFinancedDocNumbers.has(d.documentNumber)
      );

      console.log(`AI confirmed ${confirmedRecords.length} owner-financed deals`);

      if (confirmedRecords.length === 0) {
        toast({
          title: "No owner-financed deals confirmed",
          description: `Hard filter passed ${potentialOwnerFinanced.length}, but AI rejected all as non-individual lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Check for existing records
      const existingQuery = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', countyDbValue)
        .in('document_number', confirmedRecords.map(r => r.documentNumber));

      const existingDocs = new Set(existingQuery.data?.map(d => d.document_number) || []);
      const newRecords = confirmedRecords.filter(r => !existingDocs.has(r.documentNumber));

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${confirmedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Insert
      const recordsToInsert = newRecords.map(record => ({
        county: countyDbValue,
        document_number: record.documentNumber,
        filing_date: record.filingDate,
        document_type: record.docType,
        grantor_name: record.grantors.join('; '),
        grantee_name: record.lender, // The identified individual lender
        legal_description: record.legalDescription,
        parcel_id: record.parcelId,
        is_potential_owner_finance: true,
        confidence_score: 80,
        detection_reasons: ['AI confirmed individual beneficiary/lender'],
        status: 'new',
        raw_data: { grantors: record.grantors, grantees: record.grantees, lender: record.lender } as any,
      }));

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(recordsToInsert, { onConflict: 'document_number', ignoreDuplicates: false });

      if (insertError) throw insertError;

      backupRecordsToExcel(recordsToInsert, countyName, `${county}_index_parser`);

      toast({
        title: "Success!",
        description: `Saved ${newRecords.length} owner-financed deals!\n` +
          `Total: ${documents.length} | Corporate: ${corporateCount} | ` +
          `AI confirmed: ${confirmedRecords.length} | New saved: ${newRecords.length}`,
      });

      setTextData("");
      onRecordsSaved();
    } catch (error) {
      console.error('Error processing Whatcom index data:', error);
      toast({ title: "Error", description: "Failed to process records. Please try again.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const previewCount = textData.trim() ? parseWhatcomData(textData).length : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{countyName} County, WA - Index Text Parser with AI Filtering</CardTitle>
        <Button
          size="sm"
          onClick={handleProcess}
          disabled={isProcessing || !textData.trim()}
        >
          {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Process{previewCount > 0 ? ` (${previewCount})` : ''}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste indexed Deed of Trust records from the {countyName} County search results.
            Copy multiple record blocks — each starting with a document number like <code>2023-1201548</code>.
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>How it works:</strong> Title companies, MERS, law firms, and trustee services are stripped from grantees
            to identify the real lender. Corporate/institutional lenders are filtered out. Remaining records go through AI verification.
          </p>
        </div>

        <Textarea
          value={textData}
          onChange={(e) => setTextData(e.target.value)}
          placeholder={county.toLowerCase() === 'sanjuan'
            ? "2023-0103007  Date Recorded: 01/03/2023 02:26 PM Doc Type: DEED OF TRUST\nDIRECT: SPRINKLE, SETH GORDON\nINDIRECT: BOEING EMPLOYEES' CREDIT UNION\nTax# 152950174000 Sub CENTER ISLAND Lot 174"
            : "2023-1201548  Date Recorded: 12/21/2023 03:04 PM Doc Type: Deed of Trust\nGRANTOR: VELDHUISEN, JAMIE G\nGRANTEE: STREMLER, KENNETH\nGRANTEE: WHATCOM LAND TITLE\nAPN# 4003200150700000 QQ SW/SW Sct 20 Twn 40 Rng 03E"}
          className="min-h-[300px] font-mono text-sm"
        />

        {previewCount > 0 && (
          <p className="text-sm text-muted-foreground">
            Detected <strong>{previewCount}</strong> DEED OF TRUST records to analyze
          </p>
        )}

        <Button
          onClick={handleProcess}
          disabled={isProcessing || !textData.trim()}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Filtering & Analyzing...
            </>
          ) : (
            `Filter & Save Owner-Financed Deals${previewCount > 0 ? ` (${previewCount} records)` : ''}`
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
